/**
 * @fileoverview Table header configuration for the "Hold" auth status tab.
 *
 * This module defines the column structure for displaying medical PA orders
 * that are currently on hold. It includes patient details with a left border
 * indicator, date of work, plan details, medication, provider, DOS, BO value,
 * hold until date, RPA status indicators, and a side modal icon.
 *
 * @module holdTableHeaders
 */

import { TableCellType } from "../../../../components/custom-table/table";
import { GenericAuthStatusTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { MedicalPaOrdersAlgoliaSortFields } from "../../medicalPaOrdersAlgolia/utils/medicalPaOrdersTable";

/**
 * Returns the table header configuration for the "Hold" auth status tab.
 *
 * @description
 * This configuration is specifically designed for orders in the hold state.
 * Key features:
 * - Patient details column includes a left border indicator for visual distinction
 * - "Hold Until" column shows when the hold period expires
 * - Includes side modal icon for quick actions
 * - Total column widths sum to 100%
 *
 * @returns {Array<Object>} Array of table header configuration objects
 *
 * @example
 * const headers = getHoldTableHeaders();
 * // Use headers to render the table
 */
export const getHoldTableHeaders = () => {
  return [
    {
      label: "Patient Name",
      key: GenericAuthStatusTableColumnKeys.PATIENT_DETAILS_WITH_LEFT_BORDER,
      order: 1,
      width: 12,
      sortable: true,
      type: TableCellType.MULTILINE,
      subKey: "mainText",
      sortField: MedicalPaOrdersAlgoliaSortFields.FIRST_NAME,
    },
    {
      label: "Date of Work",
      key: GenericAuthStatusTableColumnKeys.DATE_OF_WORK,
      order: 2,
      width: 10,
      sortable: true,
      type: TableCellType.DATE,
      sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_WORK,
    },
    {
      label: "Plan Details",
      key: GenericAuthStatusTableColumnKeys.PLAN_DETAILS,
      order: 3,
      width: 14,
      sortable: true,
      type: TableCellType.MULTILINE,
      subKey: "mainText",
      sortField: MedicalPaOrdersAlgoliaSortFields.PRIMARY_PAYER_NAME,
    },
    {
      label: "Medication",
      key: GenericAuthStatusTableColumnKeys.MEDICATION,
      order: 4,
      width: 10,
      sortable: true,
      type: TableCellType.STRING,
      sortField: MedicalPaOrdersAlgoliaSortFields.REGIMEN_NAME,
    },
    {
      label: "Provider",
      key: GenericAuthStatusTableColumnKeys.PROVIDER,
      order: 5,
      width: 10,
      sortable: true,
      type: TableCellType.STRING,
      sortField: MedicalPaOrdersAlgoliaSortFields.PRACTITIONER_NAME,
    },
    {
      label: "DOS",
      key: GenericAuthStatusTableColumnKeys.DATE_OF_SERVICE,
      order: 6,
      width: 8,
      sortable: true,
      type: TableCellType.DATE,
      sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_SERVICE,
    },
    {
      label: "BO Value",
      key: GenericAuthStatusTableColumnKeys.BO_VALUE,
      order: 7,
      width: 10,
      type: TableCellType.BADGE,
    },
    {
      label: "Hold Until",
      key: GenericAuthStatusTableColumnKeys.HOLD_UNTIL,
      order: 8,
      width: 8,
      type: TableCellType.STRING,
    },
    {
      label: "",
      key: GenericAuthStatusTableColumnKeys.RPA_STATUS,
      order: 9,
      width: 4,
      type: TableCellType.RPA_STATUS,
    },
    {
      label: "",
      key: GenericAuthStatusTableColumnKeys.RPA_STATUS_TWO,
      order: 10,
      width: 4,
      type: TableCellType.RPA_STATUS_TWO,
    },
    {
      label: "",
      key: GenericAuthStatusTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS,
      order: 11,
      width: 7,
      type: TableCellType.OVERLAPPING_ICONS_WITH_POPOVER_AND_ACTIONS,
    },
    {
      label: "",
      key: GenericAuthStatusTableColumnKeys.SIDE_MODAL_ICON,
      order: 12,
      width: 3,
      type: TableCellType.SIDE_MODAL_ICON,
    },
  ];
};
